<script lang="ts">
  import { createContext } from 'reka-ui'
  import { useSidebar } from './sidebar-provider.vue'

  export const [useSidebarMenuCollapsible, provideSidebarMenuCollapsible] =
    createContext<{
      isOpen: Ref<boolean>
      setIsOpen: (isOpen: boolean) => void
      setIsActiveRoute: (isActiveRoute: boolean) => void
    }>('sidebar-menu-collapsible')
</script>

<script setup lang="ts">
  import { type IconValues } from '@gat-ship/icons'
  import { computed, provide, ref, type Ref } from 'vue'
  import {
    Popover,
    PopoverTrigger,
    PopoverContent
  } from '@gat-ship/vue-components/popover'

  const props = defineProps<{
    icon?: IconValues
    text: string
  }>()

  provide('sidebar-menu-collapsible', true)

  const { isRail } = useSidebar({
    isRail: computed(() => false),
    toggleRail: () => {}
  })

  const _isActiveRoute = ref(false)

  const isOpen = ref(false)

  provideSidebarMenuCollapsible({
    isOpen,
    setIsOpen: (value: boolean) => {
      isOpen.value = value
    },
    setIsActiveRoute: (value: boolean) => {
      _isActiveRoute.value = value
    }
  })
</script>

<template>
  <div class="flex flex-col" :class="{ 'w-fit': isRail, 'w-full': !isRail }">
    <Popover v-if="isRail">
      <PopoverTrigger as-child>
        <button
          class="text-secondary-foreground hover:bg-accent/50 group relative flex w-fit select-none items-center justify-center rounded-sm p-2"
          :class="{ 'text-primary': _isActiveRoute }"
        >
          <gat-icon v-if="icon" :icon="icon" class="size-4.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        class="min-w-50 w-fit p-0"
        side="right"
        :side-offset="16"
      >
        <div class="flex-1 border-b p-2 text-sm">{{ text }}</div>
        <slot />
      </PopoverContent>
    </Popover>
    <button
      v-else
      class="text-secondary-foreground hover:bg-accent/50 group relative flex w-full select-none items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left transition duration-200 ease-out"
      @click="isOpen = !isOpen"
    >
      <gat-icon
        v-if="icon"
        :icon="icon"
        class="size-4"
        :class="{ 'text-primary': _isActiveRoute }"
      />
      <div class="flex grow items-center gap-2" data-hide-collapsed="true">
        <div class="flex-1 text-sm">{{ text }}</div>
        <gat-icon
          :icon="$icons.common.ChevronDown"
          class="transition-all-default text-soft size-4 duration-200 ease-out"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </button>
    <div
      v-if="!isRail"
      class="relative flex w-full flex-col gap-0 transition-all duration-200 ease-out"
      :class="{ 'overflow-y-hidden': !isOpen }"
      :style="{ maxHeight: isOpen ? '1000px' : '0px' }"
    >
      <div
        class="bg-border left-4.5 absolute bottom-0 top-0 z-50 my-2 w-[2px] rounded-lg"
      ></div>
      <slot />
      <!-- <div v-if="isOpen">
  <separator />
</div> -->
    </div>
  </div>
</template>

<style scoped>
  .sidebar-menu-collapsible {
    transition: max-height 0.3s ease-in-out;
    overflow: hidden;
  }
</style>
